import { Component, Input, ElementRef, ViewChild, Injector, OnInit, inject } from '@angular/core'

import {
  ControlValueAccessor,
  NgControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms'
import { IconComponent } from '../icon'
import { TypographyComponent } from '../typography'
import { IconValuesType } from '../icon/icon.component.types'
import { ErrorComponent } from '../error'

@Component({
  selector: 'hyperkit-input',
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    IconComponent,
    TypographyComponent,
    ErrorComponent
],
})
export class InputComponent implements ControlValueAccessor, OnInit {
  private injector = inject(Injector);
  private elementRef = inject(ElementRef);

  @ViewChild('inputElement') inputElement!: ElementRef<HTMLInputElement>

  @Input() label?: string
  @Input() hint?: string
  @Input() placeholder = ''
  @Input() rightIcon?: IconValuesType
  @Input() showClear = false
  @Input() maxLength?: number
  @Input() pattern?: RegExp // Optional regex pattern for validation
  @Input() type: 'text' | 'password' | 'email' | 'number' | 'phone' = 'text'
  public ngControl: NgControl | null = null

  value = ''
  isFocused = false
  disabled = false

  private onChange: (value: string) => void = (value: string) => {
    console.warn('onChange function not registered yet.', value)
  }

  private onTouched: () => void = () => {
    console.warn('onTouched function not registered yet.')
  }

  ngOnInit(): void {
    this.ngControl = this.injector.get(NgControl, null)
    if (this.ngControl) {
      this.ngControl.valueAccessor = this
    }
  }

  get isRequired(): boolean {
    return !!this.ngControl?.control?.hasValidator?.(Validators.required)
  }

  get hasError(): boolean {
    return !!this.ngControl?.errors && !!this.ngControl?.touched
  }

  get errorMessage(): string {
    if (!this.hasError) return ''

    const controlValue = this.ngControl?.value ?? ''

    if (this.pattern && !this.pattern.test(controlValue)) {
      return 'This field does not meet the required pattern'
    }

    const errors = this.ngControl?.errors
    if (errors?.['required']) return 'This field is required'
    if (errors?.['email']) return 'Please enter a valid email'
    if (errors?.['phone']) return 'Please enter a phone number'

    return ''
  }

  onInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value
    this.value = value
    this.onChange(value)
  }

  onCancel(): void {
    if (!this.disabled) {
      this.value = ''
      this.onChange('')
    }
  }

  onFocus(): void {
    this.isFocused = true
    this.onTouched()
  }

  onBlur(): void {
    this.isFocused = false
    this.onTouched()
  }

  // ControlValueAccessor implementation
  writeValue(value: string | null): void {
    this.value = value ?? ''
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled
  }
}
